import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  ClientDto,
  UpdateClientDto,
  clientsApi,
  emptyClientSigningDetails,
  handleApiError,
} from '../../services/clientsService';
import ContactsEditor, { convertToContactEntries, convertToUpdateContactDtos, ContactEntry } from '../ContactsEditor';
import { validatePhone } from '../../services/leadsService';
import { validateIdField } from '../../utils/validationUtils';
import { extractClientSigningDetails } from './ClientAdditionalDetailsSection';
import { BeneficiaryType, SubmitterSourceType } from '../../services/leadSigningService';
import CollapsibleSection from '../common/CollapsibleSection';
import { useTenantConfig } from '../../contexts/TenantConfigContext';

interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedClient: ClientDto) => void;
  client: ClientDto;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  direction: rtl;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-heavy);
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  direction: rtl;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;

  &:hover {
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.$hasError ? '#dc2626' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#dc2626' : 'var(--primary-color)'};
  }

  &:disabled {
    background: var(--background-secondary);
    cursor: not-allowed;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 8px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: var(--text-primary);

  input[type="radio"] {
    cursor: pointer;
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
`;

const GeneralError = styled.div`
  background: var(--error-light);
  color: #dc2626;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 16px;
  border: 1px solid #dc2626;
  font-size: 14px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--border-color);
  position: sticky;
  bottom: 0;
  background: white;

  @media (max-width: 767px) {
    flex-direction: column-reverse;
    gap: 8px;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'secondary' ? 'var(--border-color)' : 'var(--primary-color)'};
  color: ${props => props.variant === 'secondary' ? 'var(--text-primary)' : 'white'};
  border: none;
  padding: 12px 24px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const PasswordInput = styled(Input)`
  flex: 1;
`;

const CopyButton = styled.button<{ $success?: boolean }>`
  background: ${props => props.$success ? '#16a34a' : 'var(--primary-color)'};
  color: white;
  border: none;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: 100px;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 767px) {
    min-width: 80px;
    padding: 12px;
    font-size: 13px;
  }
`;

const InfoText = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
  font-style: italic;
`;

const InputWithCheckbox = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const SmallCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
  white-space: nowrap;
  user-select: none;

  input[type="checkbox"] {
    cursor: pointer;
  }
`;

const Select = styled.select<{ $hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.$hasError ? '#dc2626' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  background: white;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#dc2626' : 'var(--primary-color)'};
  }
`;

/* ============================================================
   תת-סעיף (visual: header דק בתוך Section ראשי)
   ============================================================ */
const SubSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 20px 0 12px 0;
  padding-bottom: 6px;
  border-bottom: 1px dashed var(--border-color);
`;

const SubSectionTitle = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
`;

/* ============================================================
   ChipRow לבחירת ערכי enum (סוג מוטב, מקור מגיש)
   ============================================================ */
const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${p => p.$active ? 'var(--primary-color)' : 'var(--border-color)'};
  border-radius: 999px;
  background: ${p => p.$active ? 'var(--primary-color)' : 'white'};
  color: ${p => p.$active ? 'white' : 'var(--text-primary)'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--primary-color);
    background: ${p => p.$active ? 'var(--primary-hover)' : 'var(--background-secondary)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* ============================================================
   תוויות מילולות לערכי enum
   ============================================================ */
const BENEFICIARY_LABEL: Record<string, string> = {
  adult: 'מבוגר',
  child: 'ילד',
  other: 'אחר',
};

const SUBMITTER_SOURCE_LABEL: Record<string, string> = {
  father: 'אבא',
  mother: 'אמא',
  spouse: 'בן/בת זוג',
  other: 'אחר',
};

const RELATIONSHIP_OPTIONS = ['בן/בת זוג', 'בן', 'בת', 'בעל', 'אישה', 'אב', 'אם', 'אח', 'אחות', 'אחר'];

const EditClientModal: React.FC<EditClientModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  client
}) => {
  const { config } = useTenantConfig();
  const bankStandingOrderEnabled = !!config?.bankStandingOrderEnabled;
  const [loading, setLoading] = useState(false);
  const [copyingPassword, setCopyingPassword] = useState(false);
  const [originalPassword, setOriginalPassword] = useState<string>('');
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [formData, setFormData] = useState<UpdateClientDto>({
    fullName: '',
    idNumber: '',
    isForeignResident: false,
    phone: '',
    email: '',
    birthDate: '',
    wasHospitalizedLastYear: false,
    hadSurgeryLastYear: false,
    submittedClaimBefore: false,
    nationalInsuranceUsername: '',
    nationalInsurancePassword: '',
    ...emptyClientSigningDetails(),
  });
  const [phoneEntries, setPhoneEntries] = useState<ContactEntry[]>([
    { id: 0, number: '', email: '', label: '', isPrimary: true, sortOrder: 0 }
  ]);
  const [errors, setErrors] = useState<{
    fullName?: string;
    idNumber?: string;
    phoneEntries?: string;
    email?: string;
    birthDate?: string;
    general?: string;
    beneficiaryName?: string;
    beneficiaryIdNumber?: string;
    bankCode?: string;
    branchNumber?: string;
    accountNumber?: string;
    bankGeneral?: string;
    payerIdNumber?: string;
  }>({});
  const [signingErrors, setSigningErrors] = useState<Record<string, string>>({});
  const [copySuccess, setCopySuccess] = useState(false);

  /**
   * שינוי סוג מוטב — מנקה את הסעיפים הלא רלוונטיים (מתואם להתנהגות LeadSigningDetails)
   */
  const handleBeneficiaryTypeChange = (next: BeneficiaryType) => {
    setFormData(prev => {
      const patch: Partial<UpdateClientDto> = { beneficiaryType: next };
      if (next === 'adult') {
        // adult — לנקות הורים
        Object.assign(patch, {
          fatherName: undefined, fatherIdNumber: undefined, fatherAddress: undefined,
          fatherCity: undefined, fatherBirthDate: undefined, isFatherForeignResident: false,
          motherName: undefined, motherIdNumber: undefined, motherAddress: undefined,
          motherCity: undefined, motherBirthDate: undefined, isMotherForeignResident: false,
        });
      } else if (next === 'child') {
        // child — לנקות בן/בת זוג
        Object.assign(patch, {
          spouseName: undefined, spouseAddress: undefined, spouseCity: undefined,
          spouseIdNumber: undefined, spouseBirthDate: undefined, isSpouseForeignResident: false,
        });
      } else {
        // other — לנקות גם הורים וגם בן/בת זוג
        Object.assign(patch, {
          fatherName: undefined, fatherIdNumber: undefined, fatherAddress: undefined,
          fatherCity: undefined, fatherBirthDate: undefined, isFatherForeignResident: false,
          motherName: undefined, motherIdNumber: undefined, motherAddress: undefined,
          motherCity: undefined, motherBirthDate: undefined, isMotherForeignResident: false,
          spouseName: undefined, spouseAddress: undefined, spouseCity: undefined,
          spouseIdNumber: undefined, spouseBirthDate: undefined, isSpouseForeignResident: false,
        });
      }
      // איפוס המגיש בכל מעבר
      Object.assign(patch, {
        submitterSourceType: undefined as SubmitterSourceType | undefined,
        submitterName: undefined, submitterRelationship: undefined,
        submitterIdNumber: undefined, submitterAddress: undefined, submitterCity: undefined,
      });
      return { ...prev, ...patch };
    });
    setSigningErrors({});
  };

  /**
   * שינוי מקור המגיש — מסנכרן אוטומטית את הפרטים מההורה/בן זוג הרלוונטי.
   * בעת מעבר ל"אחר" — מאפס את כל שדות המגיש (המשתמש ימלא ידנית).
   */
  const handleSubmitterSourceChange = (source: SubmitterSourceType) => {
    setFormData(prev => {
      const patch: Partial<UpdateClientDto> = { submitterSourceType: source };
      if (source === 'father') {
        Object.assign(patch, {
          submitterName: prev.fatherName, submitterIdNumber: prev.fatherIdNumber,
          submitterAddress: prev.fatherAddress, submitterCity: prev.fatherCity,
          submitterRelationship: 'אב',
        });
      } else if (source === 'mother') {
        Object.assign(patch, {
          submitterName: prev.motherName, submitterIdNumber: prev.motherIdNumber,
          submitterAddress: prev.motherAddress, submitterCity: prev.motherCity,
          submitterRelationship: 'אם',
        });
      } else if (source === 'spouse') {
        Object.assign(patch, {
          submitterName: prev.spouseName, submitterIdNumber: prev.spouseIdNumber,
          submitterAddress: prev.spouseAddress, submitterCity: prev.spouseCity,
          submitterRelationship: 'בן/בת זוג',
        });
      } else {
        // source === 'other' — ניקוי כל שדות המגיש כך שהמשתמש ימלא ידנית
        Object.assign(patch, {
          submitterName: undefined,
          submitterIdNumber: undefined,
          submitterAddress: undefined,
          submitterCity: undefined,
          submitterRelationship: undefined,
        });
      }
      return { ...prev, ...patch };
    });
    // ניקוי שגיאות קודמות של שדות המגיש
    setSigningErrors(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => { if (k.startsWith('submitter.')) delete next[k]; });
      return next;
    });
  };

  // אפשרויות מקור מגיש לפי טוגל
  const submitterOptions: SubmitterSourceType[] = (() => {
    switch (formData.beneficiaryType) {
      case 'child': return ['father', 'mother', 'other'];
      case 'adult': return ['spouse', 'other'];
      case 'other': return ['other'];
      default: return ['other'];
    }
  })();

  // לקוחות legacy יכולים להגיע עם submitterSourceType שלא תואם ל-beneficiaryType הנוכחי
  // (למשל source=father כשה-type='adult'). מציגים אותו ב-chips כדי שלא ייעלם.
  const submitterOptionsEffective: SubmitterSourceType[] =
    formData.submitterSourceType && !submitterOptions.includes(formData.submitterSourceType as SubmitterSourceType)
      ? [formData.submitterSourceType as SubmitterSourceType, ...submitterOptions]
      : submitterOptions;

  const submitterIsReadOnly = !!formData.submitterSourceType && formData.submitterSourceType !== 'other';

  useEffect(() => {
    if (isOpen && client) {
      // Reset original password
      setOriginalPassword('');

      setFormData({
        fullName: client.fullName,
        idNumber: client.idNumber,
        isForeignResident: client.isForeignResident ?? false,
        phone: client.phone,
        email: client.email || '',
        birthDate: client.birthDate ? client.birthDate.split('T')[0] : '',
        wasHospitalizedLastYear: client.wasHospitalizedLastYear,
        hadSurgeryLastYear: client.hadSurgeryLastYear,
        submittedClaimBefore: client.submittedClaimBefore,
        nationalInsuranceUsername: client.nationalInsuranceUsername || '',
        nationalInsurancePassword: '',
        // טעינת פרטי חתימה מהלקוח
        ...extractClientSigningDetails(client),
        // פרטי חשבון בנק להעברת קצבה - פרטי מקבל הקצבה
        beneficiaryName: client.beneficiaryName || '',
        beneficiaryIdNumber: client.beneficiaryIdNumber || '',
        bankCode: client.bankCode || '',
        branchNumber: client.branchNumber || '',
        accountNumber: client.accountNumber || '',
        // פרטי משלם הקצבה
        payerName: client.payerName || '',
        payerIdNumber: client.payerIdNumber || '',
        isPayerForeignResident: client.isPayerForeignResident ?? false,
      });

      // Initialize contact entries from client
      if (client.contacts && client.contacts.length > 0) {
        setPhoneEntries(convertToContactEntries(client.contacts));
      } else {
        // Fallback: create entry from main phone field
        setPhoneEntries([
          { id: 0, number: client.phone || '', email: '', label: 'ראשי', isPrimary: true, sortOrder: 0 }
        ]);
      }

      setErrors({});
      setSigningErrors({});
      setCopySuccess(false);

      // Load existing password if exists
      if (client.hasNationalInsurancePassword) {
        setLoadingPassword(true);
        clientsApi.getNationalInsurancePassword(client.id)
          .then(password => {
            setOriginalPassword(password);
            setFormData(prev => ({
              ...prev,
              nationalInsurancePassword: password
            }));
          })
          .catch(error => {
            console.error('Failed to load password:', error);
          })
          .finally(() => {
            setLoadingPassword(false);
          });
      }
    }
  }, [isOpen, client]);

  // Validate a single field (for onBlur validation)
  const validateField = (field: string, value: any): string => {
    switch (field) {
      case 'fullName':
        if (!value || !value.trim()) {
          return 'יש להזין שם מלא';
        } else if (value.length > 100) {
          return 'שם מלא לא יכול להכיל יותר מ-100 תווים';
        }
        break;

      case 'idNumber': {
        if (!value || !value.trim()) {
          return 'יש להזין תעודת זהות';
        }
        const idError = validateIdField(value, formData.isForeignResident ?? false);
        if (idError) return idError;
        break;
      }

      case 'email':
        if (value && value.trim()) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            return 'דוא״ל לא תקין';
          } else if (value.length > 100) {
            return 'דוא״ל לא יכול להכיל יותר מ-100 תווים';
          }
        }
        break;

      case 'birthDate':
        if (!value || !value.trim()) {
          return 'יש להזין תאריך לידה';
        }
        break;

      default:
        return '';
    }
    return '';
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    const fullNameError = validateField('fullName', formData.fullName);
    if (fullNameError) newErrors.fullName = fullNameError;

    const idNumberError = validateField('idNumber', formData.idNumber);
    if (idNumberError) newErrors.idNumber = idNumberError;

    // Validate phone entries
    const hasPrimary = phoneEntries.some(e => e.isPrimary);
    const hasValidPhone = phoneEntries.some(e => e.number.trim() !== '');
    if (!hasValidPhone) {
      newErrors.phoneEntries = 'יש להזין לפחות מספר טלפון אחד';
    } else if (!hasPrimary) {
      newErrors.phoneEntries = 'יש לסמן טלפון ראשי';
    } else {
      // Validate each phone number format
      for (const entry of phoneEntries) {
        if (entry.number.trim() && !validatePhone(entry.number)) {
          newErrors.phoneEntries = 'אחד ממספרי הטלפון אינו תקין';
          break;
        }
      }
    }

    const emailError = validateField('email', formData.email);
    if (emailError) newErrors.email = emailError;

    const birthDateError = validateField('birthDate', formData.birthDate);
    if (birthDateError) newErrors.birthDate = birthDateError;

    // ולידציה לפרטי חשבון בנק — אופציונלי, אך all-or-nothing
    const bankFields = [
      formData.beneficiaryName,
      formData.beneficiaryIdNumber,
      formData.bankCode,
      formData.branchNumber,
      formData.accountNumber,
    ].map(v => (v || '').trim());
    const anyBankFilled = bankFields.some(v => v !== '');
    const allBankFilled = bankFields.every(v => v !== '');
    if (anyBankFilled && !allBankFilled) {
      newErrors.bankGeneral = 'יש למלא את כל פרטי חשבון הבנק או להשאיר את כולם ריקים';
    }
    if (allBankFilled) {
      const digitsOnly = /^\d+$/;
      if (!digitsOnly.test((formData.bankCode || '').trim())) {
        newErrors.bankCode = 'ניתן להזין ספרות בלבד';
      }
      if (!digitsOnly.test((formData.branchNumber || '').trim())) {
        newErrors.branchNumber = 'ניתן להזין ספרות בלבד';
      }
      if (!digitsOnly.test((formData.accountNumber || '').trim())) {
        newErrors.accountNumber = 'ניתן להזין ספרות בלבד';
      }
      const beneficiaryIdError = validateIdField((formData.beneficiaryIdNumber || '').trim(), false);
      if (beneficiaryIdError) {
        newErrors.beneficiaryIdNumber = beneficiaryIdError;
      }
    }

    // ולידציה לת.ז. משלם הקצבה — רק כשאופציית הוראת קבע בנקאית מופעלת
    if (bankStandingOrderEnabled) {
      const payerIdError = validateIdField((formData.payerIdNumber || '').trim(), formData.isPayerForeignResident ?? false);
      if (payerIdError) {
        newErrors.payerIdNumber = payerIdError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (field: string, value: any) => {
    const error = validateField(field, value);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    } else {
      // Clear error if field is now valid
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof typeof errors];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Clear password only if user explicitly deleted it (was not empty originally and now is empty)
      const shouldClearPassword = originalPassword !== '' && !formData.nationalInsurancePassword;
      // Only send password if it changed
      const passwordToSend = formData.nationalInsurancePassword === originalPassword
        ? undefined
        : formData.nationalInsurancePassword;

      // Get the primary phone number
      const primaryPhone = phoneEntries.find(e => e.isPrimary && e.number.trim());
      const mainPhone = primaryPhone?.number.trim() || phoneEntries.find(e => e.number.trim())?.number.trim() || '';

      const submitData = {
        ...formData,
        phone: mainPhone,
        contacts: convertToUpdateContactDtos(phoneEntries),
        // Convert empty optional strings to undefined (null) for backend nullable types
        email: formData.email || undefined,
        birthDate: formData.birthDate || undefined,
        nationalInsuranceUsername: formData.nationalInsuranceUsername || undefined,
        nationalInsurancePassword: passwordToSend,
        clearNationalInsurancePassword: shouldClearPassword
      };
      const updatedClient = await clientsApi.updateClient(client.id, submitData);
      onSuccess(updatedClient);
      onClose();
    } catch (error) {
      console.error('Error updating client:', error);
      const msg = handleApiError(error);
      if (msg.includes('משלם הקצבה')) setErrors({ payerIdNumber: msg });
      else setErrors({ general: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!client.nationalInsuranceUsername) {
      setErrors({ general: 'לא קיים שם משתמש לביטוח לאומי' });
      return;
    }

    setCopyingPassword(true);
    setCopySuccess(false);

    try {
      const password = await clientsApi.getNationalInsurancePassword(client.id);

      if (!password) {
        setErrors({ general: 'לא נמצאה סיסמה' });
        return;
      }

      // Copy to clipboard
      await navigator.clipboard.writeText(password);

      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (error) {
      console.error('Error copying password:', error);
      setErrors({ general: handleApiError(error) });
    } finally {
      setCopyingPassword(false);
    }
  };

  // Track where mousedown started to prevent closing when selecting text
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && mouseDownTargetRef.current === e.currentTarget && !loading) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay $isOpen={isOpen} onMouseDown={handleMouseDown} onClick={handleOverlayClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>עריכת פרטי לקוח</ModalTitle>
          <CloseButton onClick={onClose} disabled={loading}>×</CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            {errors.general && (
              <GeneralError>{errors.general}</GeneralError>
            )}

            {/* Personal Information */}
            <Section>
              <CollapsibleSection title="פרטים אישיים" defaultOpen={true}>
              <InfoGrid>
                <FormGroup>
                  <Label>שם מלא *</Label>
                  <Input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => {
                      setFormData({ ...formData, fullName: e.target.value });
                      if (errors.fullName) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.fullName;
                          return newErrors;
                        });
                      }
                    }}
                    onBlur={(e) => handleBlur('fullName', e.target.value)}
                    $hasError={!!errors.fullName}
                    placeholder="שם מלא"
                    required
                  />
                  {errors.fullName && <ErrorMessage>{errors.fullName}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>תעודת זהות *</Label>
                  <InputWithCheckbox>
                    <Input
                      type="text"
                      value={formData.idNumber}
                      onChange={(e) => {
                        setFormData({ ...formData, idNumber: e.target.value });
                        if (errors.idNumber) {
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.idNumber;
                            return newErrors;
                          });
                        }
                      }}
                      onBlur={(e) => handleBlur('idNumber', e.target.value)}
                      $hasError={!!errors.idNumber}
                      placeholder="123456789"
                      maxLength={formData.isForeignResident ? 20 : 9}
                      required
                      style={{ flex: 1 }}
                    />
                    <SmallCheckboxLabel>
                      <input
                        type="checkbox"
                        checked={formData.isForeignResident ?? false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData(prev => ({ ...prev, isForeignResident: checked }));
                          // ניקוי שגיאת ת.ז. בעת שינוי הסימון - ולידציה תקפה לפי המצב החדש
                          if (errors.idNumber) {
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.idNumber;
                              return newErrors;
                            });
                          }
                        }}
                      />
                      תושב חו"ל
                    </SmallCheckboxLabel>
                  </InputWithCheckbox>
                  {errors.idNumber && <ErrorMessage>{errors.idNumber}</ErrorMessage>}
                </FormGroup>

              </InfoGrid>

              <FormGroup>
                <Label>אנשי קשר *</Label>
                <ContactsEditor
                  contacts={phoneEntries}
                  onChange={(newEntries) => {
                    setPhoneEntries(newEntries);
                    if (errors.phoneEntries) {
                      setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.phoneEntries;
                        return newErrors;
                      });
                    }
                  }}
                />
                {errors.phoneEntries && <ErrorMessage>{errors.phoneEntries}</ErrorMessage>}
              </FormGroup>

              <InfoGrid>

                <FormGroup>
                  <Label>דוא״ל</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (errors.email) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.email;
                          return newErrors;
                        });
                      }
                    }}
                    onBlur={(e) => handleBlur('email', e.target.value)}
                    $hasError={!!errors.email}
                    placeholder="example@email.com"
                  />
                  {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>תאריך לידה *</Label>
                  <Input
                    type="date"
                    value={formData.birthDate || ''}
                    onChange={(e) => {
                      setFormData({ ...formData, birthDate: e.target.value || undefined });
                      if (errors.birthDate) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.birthDate;
                          return newErrors;
                        });
                      }
                    }}
                    onBlur={(e) => handleBlur('birthDate', e.target.value)}
                    $hasError={!!errors.birthDate}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  {errors.birthDate && <ErrorMessage>{errors.birthDate}</ErrorMessage>}
                </FormGroup>
              </InfoGrid>
              </CollapsibleSection>
            </Section>

            {/* ============================================================
                פרטים נוספים — מקטע מתכווץ עם סוג מוטב + הורים / בן זוג / מגיש
                ============================================================ */}
            <Section>
              <CollapsibleSection title="פרטים נוספים" defaultOpen={false}>
              <FormGroup>
                <Label>סוג מוטב</Label>
                <ChipRow>
                  {(['adult', 'child', 'other'] as const).map(t => (
                    <Chip
                      key={t}
                      type="button"
                      $active={formData.beneficiaryType === t}
                      onClick={() => handleBeneficiaryTypeChange(t)}
                    >
                      {BENEFICIARY_LABEL[t]}
                    </Chip>
                  ))}
                </ChipRow>
              </FormGroup>

              {/* ============================================================
                  פרטי האב — מוצג בזרימת ילד, או כשקיימים נתוני אב (כדי לאפשר עריכה
                  של נתונים legacy שהתצוגה ב-ClientInfoTab כן חושפת)
                  ============================================================ */}
              {(formData.beneficiaryType === 'child'
                || formData.fatherName
                || formData.fatherIdNumber
                || formData.fatherAddress
                || formData.fatherCity
                || formData.fatherBirthDate) && (
                <>
                  <SubSectionHeader>
                    <SubSectionTitle>פרטי האב</SubSectionTitle>
                  </SubSectionHeader>
                  <InfoGrid>
                    <FormGroup>
                      <Label>שם</Label>
                      <Input
                        type="text"
                        value={formData.fatherName || ''}
                        onChange={(e) => setFormData({ ...formData, fatherName: e.target.value })}
                        placeholder="שם מלא של האב"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>תעודת זהות</Label>
                      <InputWithCheckbox>
                        <Input
                          type="text"
                          value={formData.fatherIdNumber || ''}
                          onChange={(e) => {
                            setFormData({ ...formData, fatherIdNumber: e.target.value });
                            if (signingErrors['father.idNumber']) {
                              setSigningErrors(prev => { const n = { ...prev }; delete n['father.idNumber']; return n; });
                            }
                          }}
                          onBlur={(e) => {
                            const err = validateIdField(e.target.value, formData.isFatherForeignResident);
                            setSigningErrors(prev => err ? { ...prev, 'father.idNumber': err } : prev);
                          }}
                          $hasError={!!signingErrors['father.idNumber']}
                          placeholder="123456789"
                          maxLength={9}
                          style={{ flex: 1, direction: 'ltr', textAlign: 'center' }}
                        />
                        <SmallCheckboxLabel>
                          <input
                            type="checkbox"
                            checked={formData.isFatherForeignResident}
                            onChange={(e) => {
                              const foreign = e.target.checked;
                              setFormData(prev => ({ ...prev, isFatherForeignResident: foreign }));
                              const err = validateIdField(formData.fatherIdNumber, foreign);
                              setSigningErrors(prev => err ? { ...prev, 'father.idNumber': err } : { ...prev, 'father.idNumber': '' });
                            }}
                          />
                          תושב חו"ל
                        </SmallCheckboxLabel>
                      </InputWithCheckbox>
                      {signingErrors['father.idNumber'] && <ErrorMessage>{signingErrors['father.idNumber']}</ErrorMessage>}
                    </FormGroup>
                    <FormGroup>
                      <Label>כתובת</Label>
                      <Input
                        type="text"
                        value={formData.fatherAddress || ''}
                        onChange={(e) => setFormData({ ...formData, fatherAddress: e.target.value })}
                        placeholder="רחוב ומספר בית"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>עיר</Label>
                      <Input
                        type="text"
                        value={formData.fatherCity || ''}
                        onChange={(e) => setFormData({ ...formData, fatherCity: e.target.value })}
                        placeholder="עיר מגורים"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>תאריך לידה</Label>
                      <Input
                        type="date"
                        value={formData.fatherBirthDate ? formData.fatherBirthDate.substring(0, 10) : ''}
                        onChange={(e) => setFormData({ ...formData, fatherBirthDate: e.target.value || undefined })}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </FormGroup>
                  </InfoGrid>
                </>
              )}

              {/* ============================================================
                  פרטי האם — מוצג בזרימת ילד, או כשקיימים נתוני אם
                  ============================================================ */}
              {(formData.beneficiaryType === 'child'
                || formData.motherName
                || formData.motherIdNumber
                || formData.motherAddress
                || formData.motherCity
                || formData.motherBirthDate) && (
                <>
                  <SubSectionHeader>
                    <SubSectionTitle>פרטי האם</SubSectionTitle>
                  </SubSectionHeader>
                  <InfoGrid>
                    <FormGroup>
                      <Label>שם</Label>
                      <Input
                        type="text"
                        value={formData.motherName || ''}
                        onChange={(e) => setFormData({ ...formData, motherName: e.target.value })}
                        placeholder="שם מלא של האם"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>תעודת זהות</Label>
                      <InputWithCheckbox>
                        <Input
                          type="text"
                          value={formData.motherIdNumber || ''}
                          onChange={(e) => {
                            setFormData({ ...formData, motherIdNumber: e.target.value });
                            if (signingErrors['mother.idNumber']) {
                              setSigningErrors(prev => { const n = { ...prev }; delete n['mother.idNumber']; return n; });
                            }
                          }}
                          onBlur={(e) => {
                            const err = validateIdField(e.target.value, formData.isMotherForeignResident);
                            setSigningErrors(prev => err ? { ...prev, 'mother.idNumber': err } : prev);
                          }}
                          $hasError={!!signingErrors['mother.idNumber']}
                          placeholder="123456789"
                          maxLength={9}
                          style={{ flex: 1, direction: 'ltr', textAlign: 'center' }}
                        />
                        <SmallCheckboxLabel>
                          <input
                            type="checkbox"
                            checked={formData.isMotherForeignResident}
                            onChange={(e) => {
                              const foreign = e.target.checked;
                              setFormData(prev => ({ ...prev, isMotherForeignResident: foreign }));
                              const err = validateIdField(formData.motherIdNumber, foreign);
                              setSigningErrors(prev => err ? { ...prev, 'mother.idNumber': err } : { ...prev, 'mother.idNumber': '' });
                            }}
                          />
                          תושב חו"ל
                        </SmallCheckboxLabel>
                      </InputWithCheckbox>
                      {signingErrors['mother.idNumber'] && <ErrorMessage>{signingErrors['mother.idNumber']}</ErrorMessage>}
                    </FormGroup>
                    <FormGroup>
                      <Label>כתובת</Label>
                      <Input
                        type="text"
                        value={formData.motherAddress || ''}
                        onChange={(e) => setFormData({ ...formData, motherAddress: e.target.value })}
                        placeholder="רחוב ומספר בית"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>עיר</Label>
                      <Input
                        type="text"
                        value={formData.motherCity || ''}
                        onChange={(e) => setFormData({ ...formData, motherCity: e.target.value })}
                        placeholder="עיר מגורים"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>תאריך לידה</Label>
                      <Input
                        type="date"
                        value={formData.motherBirthDate ? formData.motherBirthDate.substring(0, 10) : ''}
                        onChange={(e) => setFormData({ ...formData, motherBirthDate: e.target.value || undefined })}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </FormGroup>
                  </InfoGrid>
                </>
              )}

              {/* ============================================================
                  פרטי בן/בת זוג — בזרימת מבוגר, או כשקיימים נתוני בן-זוג
                  ============================================================ */}
              {(formData.beneficiaryType === 'adult'
                || formData.spouseName
                || formData.spouseIdNumber
                || formData.spouseAddress
                || formData.spouseCity
                || formData.spouseBirthDate) && (
                <>
                  <SubSectionHeader>
                    <SubSectionTitle>פרטי בן/בת זוג</SubSectionTitle>
                  </SubSectionHeader>
                  <InfoGrid>
                    <FormGroup>
                      <Label>שם</Label>
                      <Input
                        type="text"
                        value={formData.spouseName || ''}
                        onChange={(e) => setFormData({ ...formData, spouseName: e.target.value })}
                        placeholder="שם מלא"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>תעודת זהות</Label>
                      <InputWithCheckbox>
                        <Input
                          type="text"
                          value={formData.spouseIdNumber || ''}
                          onChange={(e) => {
                            setFormData({ ...formData, spouseIdNumber: e.target.value });
                            if (signingErrors['spouse.idNumber']) {
                              setSigningErrors(prev => { const n = { ...prev }; delete n['spouse.idNumber']; return n; });
                            }
                          }}
                          onBlur={(e) => {
                            const err = validateIdField(e.target.value, formData.isSpouseForeignResident);
                            setSigningErrors(prev => err ? { ...prev, 'spouse.idNumber': err } : prev);
                          }}
                          $hasError={!!signingErrors['spouse.idNumber']}
                          placeholder="123456789"
                          maxLength={9}
                          style={{ flex: 1, direction: 'ltr', textAlign: 'center' }}
                        />
                        <SmallCheckboxLabel>
                          <input
                            type="checkbox"
                            checked={formData.isSpouseForeignResident}
                            onChange={(e) => {
                              const foreign = e.target.checked;
                              setFormData(prev => ({ ...prev, isSpouseForeignResident: foreign }));
                              const err = validateIdField(formData.spouseIdNumber, foreign);
                              setSigningErrors(prev => err ? { ...prev, 'spouse.idNumber': err } : { ...prev, 'spouse.idNumber': '' });
                            }}
                          />
                          תושב חו"ל
                        </SmallCheckboxLabel>
                      </InputWithCheckbox>
                      {signingErrors['spouse.idNumber'] && <ErrorMessage>{signingErrors['spouse.idNumber']}</ErrorMessage>}
                    </FormGroup>
                    <FormGroup>
                      <Label>כתובת</Label>
                      <Input
                        type="text"
                        value={formData.spouseAddress || ''}
                        onChange={(e) => setFormData({ ...formData, spouseAddress: e.target.value })}
                        placeholder="רחוב ומספר בית"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>עיר</Label>
                      <Input
                        type="text"
                        value={formData.spouseCity || ''}
                        onChange={(e) => setFormData({ ...formData, spouseCity: e.target.value })}
                        placeholder="עיר מגורים"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>תאריך לידה</Label>
                      <Input
                        type="date"
                        value={formData.spouseBirthDate ? formData.spouseBirthDate.substring(0, 10) : ''}
                        onChange={(e) => setFormData({ ...formData, spouseBirthDate: e.target.value || undefined })}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </FormGroup>
                  </InfoGrid>
                </>
              )}

              {/* ============================================================
                  מגיש הבקשה — מוצג ברגע שנבחר טוגל, או כשקיימים נתוני מגיש
                  ============================================================ */}
              {(formData.beneficiaryType
                || formData.submitterName
                || formData.submitterIdNumber
                || formData.submitterRelationship) && (
                <>
                  <SubSectionHeader>
                    <SubSectionTitle>מגיש הבקשה</SubSectionTitle>
                  </SubSectionHeader>

                  <FormGroup>
                    <Label>מקור פרטי המגיש</Label>
                    <ChipRow>
                      {submitterOptionsEffective.map(s => (
                        <Chip
                          key={s}
                          type="button"
                          $active={formData.submitterSourceType === s}
                          onClick={() => handleSubmitterSourceChange(s)}
                        >
                          {SUBMITTER_SOURCE_LABEL[s]}
                        </Chip>
                      ))}
                    </ChipRow>
                  </FormGroup>

                  <InfoGrid>
                    <FormGroup>
                      <Label>שם המגיש</Label>
                      <Input
                        type="text"
                        value={formData.submitterName || ''}
                        onChange={(e) => setFormData({ ...formData, submitterName: e.target.value })}
                        placeholder="שם מלא"
                        readOnly={submitterIsReadOnly}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>תעודת זהות</Label>
                      <Input
                        type="text"
                        value={formData.submitterIdNumber || ''}
                        onChange={(e) => {
                          setFormData({ ...formData, submitterIdNumber: e.target.value });
                          if (signingErrors['submitter.idNumber']) {
                            setSigningErrors(prev => { const n = { ...prev }; delete n['submitter.idNumber']; return n; });
                          }
                        }}
                        onBlur={(e) => {
                          if (!submitterIsReadOnly) {
                            const err = validateIdField(e.target.value, false);
                            setSigningErrors(prev => err ? { ...prev, 'submitter.idNumber': err } : prev);
                          }
                        }}
                        $hasError={!!signingErrors['submitter.idNumber']}
                        placeholder="123456789"
                        maxLength={9}
                        readOnly={submitterIsReadOnly}
                        style={{ direction: 'ltr', textAlign: 'center' }}
                      />
                      {signingErrors['submitter.idNumber'] && <ErrorMessage>{signingErrors['submitter.idNumber']}</ErrorMessage>}
                    </FormGroup>
                    <FormGroup>
                      <Label>כתובת</Label>
                      <Input
                        type="text"
                        value={formData.submitterAddress || ''}
                        onChange={(e) => setFormData({ ...formData, submitterAddress: e.target.value })}
                        placeholder="רחוב ומספר בית"
                        readOnly={submitterIsReadOnly}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>עיר</Label>
                      <Input
                        type="text"
                        value={formData.submitterCity || ''}
                        onChange={(e) => setFormData({ ...formData, submitterCity: e.target.value })}
                        placeholder="עיר מגורים"
                        readOnly={submitterIsReadOnly}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>קרבת משפחה למבוטח</Label>
                      <Select
                        value={formData.submitterRelationship || ''}
                        onChange={(e) => setFormData({ ...formData, submitterRelationship: e.target.value || undefined })}
                      >
                        <option value="">בחר קרבת משפחה</option>
                        {RELATIONSHIP_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </Select>
                    </FormGroup>
                  </InfoGrid>
                </>
              )}
              </CollapsibleSection>
            </Section>

            {/* Bank Account for Pension Transfer — מתחת ל"פרטים נוספים", מקטע מתכווץ */}
            <Section>
              <CollapsibleSection title="פרטי חשבון בנק להעברת קצבה" defaultOpen={false}>
              {errors.bankGeneral && (
                <GeneralError style={{ marginBottom: 12 }}>{errors.bankGeneral}</GeneralError>
              )}
              <SubSectionHeader>
                <SubSectionTitle>פרטי מקבל הקצבה</SubSectionTitle>
              </SubSectionHeader>
              <InfoGrid>
                <FormGroup>
                  <Label>שם מוטב</Label>
                  <Input
                    type="text"
                    value={formData.beneficiaryName || ''}
                    onChange={(e) => {
                      setFormData({ ...formData, beneficiaryName: e.target.value });
                      if (errors.beneficiaryName || errors.bankGeneral) {
                        setErrors(prev => {
                          const n = { ...prev };
                          delete n.beneficiaryName;
                          delete n.bankGeneral;
                          return n;
                        });
                      }
                    }}
                    $hasError={!!errors.beneficiaryName}
                    placeholder="שם בעל החשבון"
                    maxLength={100}
                  />
                  {errors.beneficiaryName && <ErrorMessage>{errors.beneficiaryName}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>ת.ז. מוטב</Label>
                  <Input
                    type="text"
                    value={formData.beneficiaryIdNumber || ''}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 9);
                      setFormData({ ...formData, beneficiaryIdNumber: v });
                      if (errors.beneficiaryIdNumber || errors.bankGeneral) {
                        setErrors(prev => {
                          const n = { ...prev };
                          delete n.beneficiaryIdNumber;
                          delete n.bankGeneral;
                          return n;
                        });
                      }
                    }}
                    $hasError={!!errors.beneficiaryIdNumber}
                    placeholder="123456789"
                    maxLength={9}
                    inputMode="numeric"
                  />
                  {errors.beneficiaryIdNumber && <ErrorMessage>{errors.beneficiaryIdNumber}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>קוד בנק</Label>
                  <Input
                    type="text"
                    value={formData.bankCode || ''}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      setFormData({ ...formData, bankCode: v });
                      if (errors.bankCode || errors.bankGeneral) {
                        setErrors(prev => {
                          const n = { ...prev };
                          delete n.bankCode;
                          delete n.bankGeneral;
                          return n;
                        });
                      }
                    }}
                    $hasError={!!errors.bankCode}
                    placeholder="לדוגמה: 12"
                    maxLength={10}
                    inputMode="numeric"
                  />
                  {errors.bankCode && <ErrorMessage>{errors.bankCode}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>מספר סניף</Label>
                  <Input
                    type="text"
                    value={formData.branchNumber || ''}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      setFormData({ ...formData, branchNumber: v });
                      if (errors.branchNumber || errors.bankGeneral) {
                        setErrors(prev => {
                          const n = { ...prev };
                          delete n.branchNumber;
                          delete n.bankGeneral;
                          return n;
                        });
                      }
                    }}
                    $hasError={!!errors.branchNumber}
                    placeholder="לדוגמה: 123"
                    maxLength={10}
                    inputMode="numeric"
                  />
                  {errors.branchNumber && <ErrorMessage>{errors.branchNumber}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>מספר חשבון</Label>
                  <Input
                    type="text"
                    value={formData.accountNumber || ''}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      setFormData({ ...formData, accountNumber: v });
                      if (errors.accountNumber || errors.bankGeneral) {
                        setErrors(prev => {
                          const n = { ...prev };
                          delete n.accountNumber;
                          delete n.bankGeneral;
                          return n;
                        });
                      }
                    }}
                    $hasError={!!errors.accountNumber}
                    placeholder="מספר חשבון"
                    maxLength={30}
                    inputMode="numeric"
                  />
                  {errors.accountNumber && <ErrorMessage>{errors.accountNumber}</ErrorMessage>}
                </FormGroup>
              </InfoGrid>

              {bankStandingOrderEnabled && (
              <>
              <SubSectionHeader>
                <SubSectionTitle>פרטי משלם הקצבה</SubSectionTitle>
              </SubSectionHeader>
              <InfoGrid>
                <FormGroup>
                  <Label>שם משלם הקצבה</Label>
                  <Input
                    type="text"
                    value={formData.payerName || ''}
                    onChange={(e) => setFormData({ ...formData, payerName: e.target.value })}
                    placeholder="שם משלם הקצבה"
                    maxLength={100}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>ת.ז. משלם הקצבה</Label>
                  <InputWithCheckbox>
                    <Input
                      type="text"
                      value={formData.payerIdNumber || ''}
                      onChange={(e) => {
                        const v = formData.isPayerForeignResident
                          ? e.target.value
                          : e.target.value.replace(/\D/g, '').slice(0, 9);
                        setFormData({ ...formData, payerIdNumber: v });
                        if (errors.payerIdNumber) {
                          setErrors(prev => { const n = { ...prev }; delete n.payerIdNumber; return n; });
                        }
                      }}
                      onBlur={(e) => {
                        const err = validateIdField(e.target.value, formData.isPayerForeignResident ?? false);
                        setErrors(prev => err ? { ...prev, payerIdNumber: err } : prev);
                      }}
                      $hasError={!!errors.payerIdNumber}
                      placeholder="123456789"
                      maxLength={formData.isPayerForeignResident ? 20 : 9}
                      inputMode="numeric"
                      style={{ flex: 1 }}
                    />
                    <SmallCheckboxLabel>
                      <input
                        type="checkbox"
                        checked={formData.isPayerForeignResident ?? false}
                        onChange={(e) => {
                          const foreign = e.target.checked;
                          setFormData(prev => ({ ...prev, isPayerForeignResident: foreign }));
                          const err = validateIdField(formData.payerIdNumber, foreign);
                          setErrors(prev => {
                            const n = { ...prev };
                            if (err) n.payerIdNumber = err; else delete n.payerIdNumber;
                            return n;
                          });
                        }}
                      />
                      תושב חו"ל
                    </SmallCheckboxLabel>
                  </InputWithCheckbox>
                  {errors.payerIdNumber && <ErrorMessage>{errors.payerIdNumber}</ErrorMessage>}
                </FormGroup>
              </InfoGrid>
              </>
              )}
              </CollapsibleSection>
            </Section>

            {/* Medical Questions */}
            <Section>
              <SectionTitle>שאלות רפואיות כלליות</SectionTitle>

              <FormGroup>
                <Label>האם אושפזת בשנה האחרונה?</Label>
                <RadioGroup>
                  <RadioOption>
                    <input
                      type="radio"
                      name="wasHospitalizedLastYear"
                      checked={formData.wasHospitalizedLastYear === true}
                      onChange={() => setFormData({ ...formData, wasHospitalizedLastYear: true })}
                    />
                    כן
                  </RadioOption>
                  <RadioOption>
                    <input
                      type="radio"
                      name="wasHospitalizedLastYear"
                      checked={formData.wasHospitalizedLastYear === false}
                      onChange={() => setFormData({ ...formData, wasHospitalizedLastYear: false })}
                    />
                    לא
                  </RadioOption>
                </RadioGroup>
              </FormGroup>

              <FormGroup>
                <Label>האם עברת ניתוח בשנה האחרונה?</Label>
                <RadioGroup>
                  <RadioOption>
                    <input
                      type="radio"
                      name="hadSurgeryLastYear"
                      checked={formData.hadSurgeryLastYear === true}
                      onChange={() => setFormData({ ...formData, hadSurgeryLastYear: true })}
                    />
                    כן
                  </RadioOption>
                  <RadioOption>
                    <input
                      type="radio"
                      name="hadSurgeryLastYear"
                      checked={formData.hadSurgeryLastYear === false}
                      onChange={() => setFormData({ ...formData, hadSurgeryLastYear: false })}
                    />
                    לא
                  </RadioOption>
                </RadioGroup>
              </FormGroup>

              <FormGroup>
                <Label>האם הגשת תביעה בעבר?</Label>
                <RadioGroup>
                  <RadioOption>
                    <input
                      type="radio"
                      name="submittedClaimBefore"
                      checked={formData.submittedClaimBefore === true}
                      onChange={() => setFormData({ ...formData, submittedClaimBefore: true })}
                    />
                    כן
                  </RadioOption>
                  <RadioOption>
                    <input
                      type="radio"
                      name="submittedClaimBefore"
                      checked={formData.submittedClaimBefore === false}
                      onChange={() => setFormData({ ...formData, submittedClaimBefore: false })}
                    />
                    לא
                  </RadioOption>
                </RadioGroup>
              </FormGroup>
            </Section>

            {/* National Insurance Credentials */}
            <Section>
              <SectionTitle>פרטי גישה לביטוח לאומי</SectionTitle>

              <FormGroup>
                <Label>שם משתמש</Label>
                <Input
                  type="text"
                  value={formData.nationalInsuranceUsername || ''}
                  onChange={(e) => setFormData({ ...formData, nationalInsuranceUsername: e.target.value })}
                  placeholder="שם משתמש לאתר ביטוח לאומי"
                />
                <InfoText>שם המשתמש לכניסה לאתר ביטוח לאומי</InfoText>
              </FormGroup>

              <FormGroup>
                <Label>סיסמה</Label>
                <PasswordInputWrapper>
                  <PasswordInput
                    type="text"
                    value={formData.nationalInsurancePassword || ''}
                    onChange={(e) => setFormData({ ...formData, nationalInsurancePassword: e.target.value })}
                    placeholder={loadingPassword ? 'טוען סיסמה...' : 'הזן סיסמה'}
                    disabled={loadingPassword}
                  />
                  <CopyButton
                    type="button"
                    onClick={handleCopyPassword}
                    disabled={copyingPassword || !formData.nationalInsurancePassword}
                    $success={copySuccess}
                  >
                    {copyingPassword ? 'מעתיק...' : copySuccess ? 'הועתק ✓' : 'העתק סיסמה'}
                  </CopyButton>
                </PasswordInputWrapper>
                <InfoText>
                  הסיסמה תוצפן אוטומטית. למחיקת הסיסמה - מחק את התוכן ושמור.
                </InfoText>
              </FormGroup>

              {client.lastNationalInsuranceCheck && (
                <FormGroup>
                  <Label>בדיקה אחרונה</Label>
                  <Input
                    type="text"
                    value={new Date(client.lastNationalInsuranceCheck).toLocaleDateString('he-IL', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    disabled
                  />
                  <InfoText>תאריך עדכון אחרון של פרטי הגישה</InfoText>
                </FormGroup>
              )}
            </Section>
          </ModalBody>

          <ButtonContainer>
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
              ביטול
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'שומר...' : 'שמירת שינויים'}
            </Button>
          </ButtonContainer>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EditClientModal;
